import { Brush, CheckCircle, DollarSign, Home, ThumbsUp, ShieldCheck, ScanLine, Calendar, Users, Ruler, CalendarClock, Hammer } from 'lucide-react';

import Head from 'next/head';
import Header from '../../components/Header';
import Hero from '../../components/Hero';
import Split from '../../components/Split';
import CTASection from '../../components/CTASection';
import Benefits from '../../components/Benefits';
import ContentBlock from '../../components/ContentBlock';
import ProcessRoadmap from '../../components/ProcessRoadmap';
import FAQ from '../../components/FAQ';
import SocialProof from '../../components/SocialProof';
import ContactUs from '../../components/ContactUs';
import Footer from '../../components/Footer';
import QuickQuoteForm from '../../components/QuickQuoteForm';
import { generateServiceSchema } from '../../utils/generateServiceSchema';
import { generateWebPageSchema } from '../../utils/generateWebPageSchema';
import { generateFAQSchema } from '../../utils/generateFAQSchema';
import FAQData from '../../data/faq/exterior-painting.js';



export default function exteriorPainting() {
  const service = {
    title: 'Exterior Painting',
    image: '/hero/best-exterior-painting-services-in-los-angeles.webp',
    description: 'Professional exterior painting by Modern Build. Flawless finishes and expert prep work. Free estimate: (323) 300-4130',
    link: '/services/exterior-painting',
  };

  const baseUrl = 'https://modernbuildremodel.com';
  const pageUrl = `${baseUrl}${service.link}`;

  const serviceSchema = generateServiceSchema(service, pageUrl);

  // Default location for static service page
  const defaultLocation = ' in Los Angeles, CA';

  // Replace <IN_CITY_ZIP> placeholders in the FAQ answers
  const mergedFaqs = FAQData.map(faq => ({
    ...faq,
    question: faq.question.replace(/<IN_CITY_ZIP>/g, defaultLocation),
    answer: faq.answer.replace(/<IN_CITY_ZIP>/g, defaultLocation),
  }));

    // Breadcrumb JSON-LD
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": baseUrl },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": `${baseUrl}/services` },
      { "@type": "ListItem", "position": 3, "name": "Exterior Painting Services", "item": pageUrl }
    ]
  };

  
  return (
    <div className="bg-white">
    <>
      <Head>
        <title>Exterior Painting Services | Free Estimate | Modern Build</title>
        <meta name="title" content="Exterior Painting Services | Free Estimate | Modern Build" />
        <meta name="description" content="Professional exterior painting by Modern Build. Flawless finishes and expert prep work. Free estimate: (323) 300-4130" />
        <meta name="keywords" content="exterior painting, house painting, residential painting, los angeles painters, home exterior, stucco paint, wood trim paint, modern build" />
        <meta name="author" content="Modern Build Home Remodeling" />
        <link rel="canonical" href="https://modernbuildremodel.com/services/exterior-painting" />

        <meta property="og:title" content="Exterior Painting Services | Free Estimate | Modern Build" />
        <meta property="og:description" content="Professional exterior painting by Modern Build. Flawless finishes and expert prep work. Free estimate: (323) 300-4130" />
        <meta property="og:image" content="https://modernbuildremodel.com/hero/best-exterior-painting-services-in-los-angeles.webp" />
        <meta property="og:url" content="https://modernbuildremodel.com/services/exterior-painting" />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Exterior Painting Services | Free Estimate | Modern Build" />
        <meta name="twitter:description" content="Professional exterior painting by Modern Build. Flawless finishes and expert prep work. Free estimate: (323) 300-4130" />
        <meta name="twitter:image" content="https://modernbuildremodel.com/hero/best-exterior-painting-services-in-los-angeles.webp" />
        <meta name="twitter:site" content="@modernbuildhome" />


        {/* JSON-LD: WebPage */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title: 'Exterior Painting Services | Free Estimate | Modern Build',
                description: 'Los Angeles exterior painting services that deliver stunning curb appeal and long-term durability. Modern Build offers free estimates and premium results.',
                slug: '/services/exterior-painting'
              })
            )
          }}
        />

        {/* JSON-LD: Breadcrumb */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

        {/* JSON-LD: Service */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />        

        {/* JSON-LD: FAQ */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateFAQSchema({
            service: service.title,
            city: 'Studio City',
            zip: '91604',
            faqs: mergedFaqs,
          }))
        }} />
      </Head>

      <Header />
      <Hero category="exteriorPainting" page="page" hideButton={true} hasContentH1={false}/>

      <QuickQuoteForm serviceTitle={service.title} />

      <Split
        subtitle="EXTERIOR PAINTING SERVICES"
        title="Upgrade Your Curb Appeal With a Flawless Finish"
        background="white"
        description="Modern Build provides expert exterior painting services that not only beautify your home but also protect it from weather and wear. Our team ensures proper surface prep, premium materials, and a smooth, durable finish every time."
        extraText="We handle stucco, wood, siding, and trim with precision, helping your home stand out while boosting its value and longevity."
        phone="(323) 300-4130"
        image1="/split/exterior-house-painting-services-los-angeles-ca.webp"
        image2="/split/house-painting-los-angeles.webp"
      />

      <Benefits
        title="What’s Included In Your Exterior Paint Job?"
        subtitle="A complete, professional service from start to finish."
        items={[
          {
            icon: <Brush className="w-8 h-8 text-primary" />,
            title: "Stucco, Wood, & Trim Painting",
          },
          {
            icon: <ScanLine className="w-8 h-8 text-primary" />,
            title: "Thorough Surface Preparation",
          },
          {
            icon: <ShieldCheck className="w-8 h-8 text-primary" />,
            title: "Weather-Resistant Finishes",
          },
          {
            icon: <Home className="w-8 h-8 text-primary" />,
            title: "Color Consultation & Matching",
          },
          {
            icon: <Calendar className="w-8 h-8 text-primary" />,
            title: "Fast Turnaround Times",
          },
          {
            icon: <CheckCircle className="w-8 h-8 text-primary" />,
            title: "Permits & HOA Approvals Handled",
          },
          {
            icon: <ThumbsUp className="w-8 h-8 text-primary" />,
            title: "Satisfaction Guaranteed",
            href: "/about-us",
          },
          {
            icon: <DollarSign className="w-8 h-8 text-primary" />,
            title: "Flexible Financing Options",
            href: "/financing",
          },
        ]}
      />

      <ContentBlock
        subtitle="EXPERT HOUSE PAINTING"
        title="Protect & Beautify Your Home"
        description="We go beyond just painting — we protect your home from the elements with high-performance materials and expert craftsmanship. Our painters are trained to work on all exterior surfaces and ensure results that last."
        extraText="From patching cracks and sealing gaps to applying even coats of premium paint, we deliver flawless results that make your home shine."
        buttonText="Request a Free Estimate"
        background="white"
      />

      <ProcessRoadmap
        title="Our Process"
        subtitle="How we deliver a superior exterior paint job"
        steps={[
          {
            icon: <Users className="w-10 h-10 text-primary" />,
            title: "Initial Consultation",
            description: "We visit your home, assess the surfaces, and provide a detailed plan based on your style and needs.",
          },
          {
            icon: <Ruler className="w-10 h-10 text-primary" />,
            title: "Color & Material Selection",
            description: "Choose from a wide range of paint options. We guide you in selecting the perfect palette and finishes.",
          },
          {
            icon: <CalendarClock className="w-10 h-10 text-primary" />,
            title: "Prep Work & Protection",
            description: "We power wash, sand, and patch surfaces as needed, then cover landscaping, windows, and fixtures for protection.",
          },
          {
            icon: <Hammer className="w-10 h-10 text-primary" />,
            title: "Painting & Final Walkthrough",
            description: "Our pros apply each coat with care and check for quality every step of the way — finishing with a final walkthrough.",
          },
        ]}
      />

      <Split
        subtitle="CUSTOM COLOR EXPERTISE"
        title="Make Your Home Stand Out"
        background="white"
        description="Modern Build helps you pick colors that complement your home’s architecture and neighborhood. Our expert color consultation and samples ensure you love the look before we start."
        extraText="From modern bold tones to timeless neutrals, we’ll guide you through a color transformation that enhances curb appeal."
        phone="(323) 300-4130"
        image1="/split/los-angeles-house-painting-services.webp"
        image2="/split/exterior-paint-los-angeles-home.webp"
        reverse={true}
      />

      <CTASection
        title="Paint Now. Pay Later."
        subtitle="0% Financing Available for Qualified Homeowners"
        buttonText="Apply for Financing"
        link="/contact"
        backgroundImage="/hero/best-home-remodling-contractor-in-los-angeles.webp"
      />

      <FAQ category="exterior-painting" />
      <SocialProof />
      <ContactUs />
      <Footer />
    </>
    </div>
  );
}
